import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit2, Trash2, X, Image as ImageIcon,
  Coins, Tag, CheckCircle2, AlertCircle, Upload, Sparkles,
  User, Check, Clock, RefreshCw, Send, ArrowRight
} from 'lucide-react';
import { giftsAPI, customIdAPI } from '../utils/api';
import AssetPreview from '../components/AssetPreview';

const GiftsManagement = () => {
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' | 'custom_ids'
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  // Custom ID requests state
  const [customRequests, setCustomRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestSearch, setRequestSearch] = useState('');
  const [offerModal, setOfferModal] = useState({ open: false, request: null, price: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    coin_value: '',
    image_url: '',
    category: 'Common',
    is_active: true
  });

  const filteredGifts = React.useMemo(() => {
    return gifts.filter(gift => 
      gift.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gift.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [gifts, searchTerm]);

  const filteredRequests = React.useMemo(() => {
    return customRequests.filter(req => {
      const q = requestSearch.toLowerCase();
      const userName = (req.user_name || req.first_name || '').toLowerCase();
      const currentId = (req.current_xoxo_id || '').toLowerCase();
      const requestedId = (req.requested_id || '').toLowerCase();
      const status = (req.status || '').toLowerCase();
      return userName.includes(q) || currentId.includes(q) || requestedId.includes(q) || status.includes(q);
    });
  }, [customRequests, requestSearch]);

  const pendingRequestsCount = React.useMemo(() => {
    return customRequests.filter(r => r.status === 'pending').length;
  }, [customRequests]);

  useEffect(() => {
    fetchGifts();
    fetchCustomRequests();
  }, []);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await giftsAPI.getGifts();
      setGifts(response.data.data || []);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch gifts from server.');
      setGifts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomRequests = async () => {
    try {
      setLoadingRequests(true);
      const res = await customIdAPI.getRequests();
      setCustomRequests(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      setUploading(true);
      setError('');
      const response = await giftsAPI.uploadGiftImage(uploadFormData);
      if (response.data.success) {
        setFormData({ ...formData, image_url: response.data.url });
        setSuccess('Asset uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error(err);
      setError('Asset upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenModal = (gift = null) => {
    if (gift) {
      setSelectedGift(gift);
      setFormData({
        name: gift.name,
        coin_value: gift.coin_value,
        image_url: gift.image_url || '',
        category: gift.category || 'Common',
        is_active: gift.is_active
      });
    } else {
      setSelectedGift(null);
      setFormData({
        name: '',
        coin_value: '',
        image_url: '',
        category: 'Common',
        is_active: true
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (selectedGift) {
        await giftsAPI.updateGift(selectedGift.id, formData);
        setSuccess('Gift updated successfully!');
      } else {
        await giftsAPI.createGift(formData);
        setSuccess('New gift added successfully!');
      }
      setShowModal(false);
      fetchGifts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save gift');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gift?')) {
      try {
        await giftsAPI.deleteGift(id);
        setGifts(gifts.filter(g => g.id !== id));
        setSuccess('Gift deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete gift');
      }
    }
  };

  const toggleStatus = async (gift) => {
    try {
      if (gift.is_active) {
        await giftsAPI.deactivateGift(gift.id);
      } else {
        await giftsAPI.activateGift(gift.id);
      }
      fetchGifts();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  // Custom ID Offer Handler
  const handleOpenOfferModal = (req) => {
    setOfferModal({
      open: true,
      request: req,
      price: req.price_coins ? String(req.price_coins) : '1000'
    });
  };

  const handleSendOffer = async (e) => {
    e.preventDefault();
    const priceNum = parseInt(offerModal.price);
    if (!priceNum || priceNum <= 0) {
      setError('Please provide a valid coin amount (greater than 0).');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await customIdAPI.offerPrice(offerModal.request.id, priceNum);
      setSuccess(`Price offer of ${priceNum} coins sent to user!`);
      setOfferModal({ open: false, request: null, price: '' });
      fetchCustomRequests();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to submit price offer.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async (req) => {
    if (!window.confirm(`Are you sure you want to reject the request for Custom ID '${req.requested_id}'?`)) {
      return;
    }
    try {
      setActionLoading(true);
      setError('');
      await customIdAPI.rejectRequest(req.id);
      setSuccess(`Request for '${req.requested_id}' has been declined.`);
      fetchCustomRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to decline request.');
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatusBadge = (status, price) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock size={12} className="animate-spin text-amber-500" /> Pending Admin Review
          </span>
        );
      case 'offered':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <Coins size={12} className="text-blue-500" /> Offer Sent: {price} Coins
          </span>
        );
      case 'purchased':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} className="text-emerald-500" /> Purchased & Active
          </span>
        );
      case 'rejected_by_user':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
            <X size={12} /> Declined by User
          </span>
        );
      case 'rejected_by_admin':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <X size={12} /> Rejected by Admin
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-slate-50 text-slate-600">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gifts & Custom ID Center</h1>
          <p className="text-slate-500 text-sm mt-1">Manage virtual gifts catalog and review user Custom ID applications.</p>
        </div>
        {activeTab === 'catalog' && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-pink-600 text-white rounded-xl font-bold hover:bg-pink-700 transition-all shadow-lg shadow-pink-100 active:scale-95"
          >
            <Plus size={18} /> Add New Gift
          </button>
        )}
        {activeTab === 'custom_ids' && (
          <button
            onClick={fetchCustomRequests}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-all shadow-md active:scale-95"
          >
            <RefreshCw size={16} className={loadingRequests ? 'animate-spin' : ''} /> Refresh Requests
          </button>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-3 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'catalog'
              ? 'bg-pink-600 text-white shadow-md shadow-pink-500/20'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Tag size={16} /> Virtual Gifts Catalog
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            activeTab === 'catalog' ? 'bg-pink-700 text-white' : 'bg-slate-200 text-slate-700'
          }`}>
            {gifts.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('custom_ids')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'custom_ids'
              ? 'bg-pink-600 text-white shadow-md shadow-pink-500/20'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Sparkles size={16} /> Custom ID Requests
          {pendingRequestsCount > 0 ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-400 text-slate-900 font-extrabold animate-pulse">
              {pendingRequestsCount} Pending
            </span>
          ) : (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === 'custom_ids' ? 'bg-pink-700 text-white' : 'bg-slate-200 text-slate-700'
            }`}>
              {customRequests.length}
            </span>
          )}
        </button>
      </div>

      {/* Notifications */}
      {success && (
        <div className="bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-in slide-in-from-top duration-300">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-in slide-in-from-top duration-300">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* TAB 1: GIFTS CATALOG */}
      {activeTab === 'catalog' && (
        <div className="space-y-6">
          {/* Search & Stats Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search by gift name or category..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-4 items-center border-l pl-4 border-slate-100">
              <div className="text-right">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Gifts</p>
                <p className="text-lg font-bold text-slate-800">{gifts.length}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600">
                <Tag size={20} />
              </div>
            </div>
          </div>

          {/* Gifts Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(n => (
                <div key={n} className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
                  <div className="w-full aspect-square bg-slate-100 rounded-xl mb-4"></div>
                  <div className="h-4 bg-slate-100 rounded w-2/3 mb-2"></div>
                  <div className="h-4 bg-slate-100 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredGifts.map((gift) => (
                <div key={gift.id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  <div className="relative aspect-square bg-slate-50 flex items-center justify-center p-6 overflow-hidden">
                    <AssetPreview
                      src={gift.image_url}
                      alt={gift.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 flex gap-1 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300">
                      <button
                        onClick={() => handleOpenModal(gift)}
                        className="p-2 bg-white text-slate-700 rounded-lg shadow-lg hover:text-pink-600 transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(gift.id)}
                        className="p-2 bg-white text-slate-700 rounded-lg shadow-lg hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-[10px] font-bold text-slate-600 uppercase border border-white">
                      {gift.category}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-slate-800">{gift.name}</h3>
                      <div className="flex items-center gap-1 text-yellow-600 font-bold text-sm">
                        <Coins size={14} /> {gift.coin_value}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={() => toggleStatus(gift)}
                        className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${gift.is_active
                          ? 'bg-green-50 text-green-600 hover:bg-green-100'
                          : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                          }`}
                      >
                        {gift.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && filteredGifts.length === 0 && (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
              <div className="w-16 h-16 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-800">No gifts found</h3>
              <p className="text-slate-500">Try adjusting your search or add a new gift.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: CUSTOM ID REQUESTS */}
      {activeTab === 'custom_ids' && (
        <div className="space-y-6">
          {/* Filter Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search user, current ID, or requested Custom ID..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none text-sm"
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 text-xs font-medium text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span> Pending Offer</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Offer Sent</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Purchased</span>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {loadingRequests ? (
              <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                <RefreshCw size={28} className="animate-spin text-pink-500" />
                <p className="text-sm font-semibold">Loading Custom ID requests...</p>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">No requests found</h3>
                <p className="text-slate-500 text-sm">When users apply for Custom IDs via mobile app, they will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      <th className="py-4 px-6">User Profile</th>
                      <th className="py-4 px-6">Current ID</th>
                      <th className="py-4 px-6">Requested Custom ID</th>
                      <th className="py-4 px-6">User Coin Balance</th>
                      <th className="py-4 px-6">Status / Offer</th>
                      <th className="py-4 px-6">Submitted At</th>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {filteredRequests.map((req) => (
                      <tr key={req.id} className="hover:bg-slate-50/50 transition-colors">
                        {/* User Profile */}
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                              {req.avatar_url ? (
                                <img src={req.avatar_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <User size={18} className="text-slate-400" />
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-slate-800">
                                {req.user_name || `${req.first_name || ''} ${req.last_name || ''}`.trim() || 'User'}
                              </p>
                              <p className="text-xs text-slate-400">{req.email || `ID: ${req.user_id}`}</p>
                            </div>
                          </div>
                        </td>

                        {/* Current ID */}
                        <td className="py-4 px-6">
                          <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md font-semibold">
                            {req.current_xoxo_id || 'None'}
                          </span>
                        </td>

                        {/* Requested Custom ID */}
                        <td className="py-4 px-6">
                          <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 text-pink-700 px-3 py-1 rounded-lg font-bold font-mono text-sm shadow-sm">
                            <Sparkles size={14} className="text-pink-500" />
                            {req.requested_id}
                          </div>
                        </td>

                        {/* User Coin Balance */}
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-1.5 font-bold text-amber-600">
                            <Coins size={14} />
                            <span>{(req.user_coins || 0).toLocaleString()}</span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-4 px-6">
                          {renderStatusBadge(req.status, req.price_coins)}
                        </td>

                        {/* Submitted At */}
                        <td className="py-4 px-6 text-xs text-slate-500 whitespace-nowrap">
                          {new Date(req.created_at).toLocaleString()}
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-6 text-right whitespace-nowrap">
                          {(req.status === 'pending' || req.status === 'offered') ? (
                            <div className="inline-flex items-center gap-2">
                              <button
                                onClick={() => handleOpenOfferModal(req)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-600 hover:bg-pink-700 text-white rounded-lg text-xs font-bold transition shadow-sm"
                              >
                                <Coins size={13} />
                                {req.status === 'offered' ? 'Update Price' : 'Accept & Set Price'}
                              </button>
                              <button
                                onClick={() => handleRejectRequest(req)}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-xs font-bold transition"
                              >
                                <X size={13} />
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">No actions needed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Offer Price Modal */}
      {offerModal.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl p-6 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center">
                  <Coins size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Set Custom ID Price</h3>
                  <p className="text-xs text-slate-500">Assign coins required for user to purchase this ID</p>
                </div>
              </div>
              <button
                onClick={() => setOfferModal({ open: false, request: null, price: '' })}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSendOffer} className="space-y-4 pt-4">
              <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-100 flex items-center justify-between text-xs">
                <div>
                  <p className="text-slate-500">Requested Custom ID</p>
                  <p className="font-mono font-bold text-base text-pink-600 mt-0.5">{offerModal.request?.requested_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500">User Wallet Balance</p>
                  <p className="font-bold text-amber-600 text-sm flex items-center justify-end gap-1 mt-0.5">
                    <Coins size={14} /> {(offerModal.request?.user_coins || 0).toLocaleString()} Coins
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Coin Price for this ID
                </label>
                <div className="relative">
                  <Coins className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="number"
                    min="1"
                    required
                    value={offerModal.price}
                    onChange={(e) => setOfferModal({ ...offerModal, price: e.target.value })}
                    placeholder="e.g. 5000"
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none font-bold text-slate-800"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  When submitted, a notification will be pushed to the user. Once they accept, this amount of coins will be deducted and the ID will be automatically generated and assigned!
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setOfferModal({ open: false, request: null, price: '' })}
                  className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-pink-600 hover:bg-pink-700 text-white rounded-xl font-bold transition text-sm flex items-center justify-center gap-2 shadow-lg shadow-pink-500/20 disabled:opacity-50"
                >
                  {actionLoading ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
                  Send Offer to User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upsert Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md max-h-[90vh] rounded-3xl shadow-2xl relative z-10 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h2 className="text-xl font-bold text-slate-800">{selectedGift ? 'Edit Gift' : 'Add New Gift'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-slate-200">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Gift Name</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all"
                    placeholder="e.g. Heart, Diamond, Rose"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Coin Value</label>
                    <div className="relative">
                      <Coins className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="number"
                        required
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all"
                        placeholder="0"
                        value={formData.coin_value}
                        onChange={(e) => setFormData({ ...formData, coin_value: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Category</label>
                    <select
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="Common">Common</option>
                      <option value="Rare">Rare</option>
                      <option value="Epic">Epic</option>
                      <option value="Legendary">Legendary</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Gift Asset (.png, .gif, .mp4, .svga, .svg) / URL</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all text-xs"
                        placeholder="https://... or upload file"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      />
                    </div>
                    <label className={`px-4 py-2.5 rounded-xl font-bold text-xs whitespace-nowrap cursor-pointer flex items-center gap-1.5 transition ${
                      uploading ? 'bg-slate-300 text-slate-600' : 'bg-pink-600 text-white hover:bg-pink-700 shadow-md shadow-pink-500/20'
                    }`}>
                      <Upload size={14} className={uploading ? 'animate-bounce' : ''} />
                      {uploading ? 'Uploading...' : 'Browse'}
                      <input
                        type="file"
                        accept="image/*,video/*,.svga,.svg"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>

                {formData.image_url && (
                  <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 mx-auto flex items-center justify-center bg-slate-50">
                    <AssetPreview
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-contain p-1"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-lg"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-3 py-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                    className={`w-12 h-6 rounded-full relative transition-colors ${formData.is_active ? 'bg-pink-600' : 'bg-slate-200'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-7' : 'left-1'}`}></div>
                  </button>
                  <span className="text-sm font-medium text-slate-600">Active Status</span>
                </div>
              </div>

              {/* Modal Footer (Always Visible) */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
                >
                  {selectedGift ? 'Save Changes' : 'Create Gift'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftsManagement;